import { useAppDispatch } from "../../../app/hooks";
import { useNavigate } from "react-router-dom";
import { classroomSlice } from "../../../redux/classroom/classroomSlice";
import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/user/userSlice";
import { hasPermission, PermissionId } from "../../../app/permissions";
import AddIcon from "@mui/icons-material/Add";
import PrizaAvatarGrid, { PrizaAvatarGridItem } from "../../../features/base/grid/PrizaAvatarGrid";
import { assertThumbnailUrl, isoToBrt, LoadState } from "../../../redux/common";
import { retrieveSchoolDpto, schoolDptosSlice, selectSchoolDptos } from "../../../redux/school/schoolDptosSlice";
import { useEffect, useState } from "react";
import { isTokenReady } from "../../../features/user/UserAvatar";
import { dptoSlice } from "../../../redux/dpto/dptoSlice";
import { routeDpto, routeDptoNew } from "../../../routes/dpto";
import { retrieveSchoolStudents, schoolStudentsSlice, selectSchoolStudents } from "../../../redux/school/schoolStudentsSlice";
import { Box, Button, ButtonGroup, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { studentSlice } from "../../../redux/student/studentSlice";
import { routeStudent } from "../../../routes/student";
import BadgedAvatar from "../../../features/base/BadgedAvatar";
import { ElementSearchBox } from "../../../features/base/ElementSearchBox";
import UserStack from "../../../features/user/UserStack";

interface ContentListProps {
    schoolId: string;
}

const SchoolStudentList = ({ schoolId }: ContentListProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const user = useSelector(selectUser);
    const students = useSelector(selectSchoolStudents);
    const [filterStudent, setFilterStudent] = useState("");

    useEffect(() => {
        if (students.request?.schoolId && students.request?.schoolId != schoolId) {
            dispatch(schoolStudentsSlice.actions.clean());
        }
        if (students.state.state == LoadState.NotStarted && isTokenReady(user))
            dispatch(retrieveSchoolStudents({ schoolId, filter: filterStudent, getClassroom: true, getFamily: true }));
    }, [students, schoolId, user, filterStudent]);


    const list = students.list || [];

    const table = true;
    const buildContent = !table ? undefined : () => {
        return <>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell align="left" sx={{
                            color: (theme) => theme.palette.primary.contrastText,
                            backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Aluno</TableCell>
                        <TableCell align="left" sx={{
                            color: (theme) => theme.palette.primary.contrastText,
                            backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Apelido</TableCell>
                        <TableCell align="left" sx={{
                            color: (theme) => theme.palette.primary.contrastText,
                            backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Nascimento</TableCell>
                        <TableCell align="left" sx={{
                            color: (theme) => theme.palette.primary.contrastText,
                            backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Matricula</TableCell>
                        <TableCell align="left" sx={{
                            color: (theme) => theme.palette.primary.contrastText,
                            backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Salas</TableCell>
                        <TableCell align="left" sx={{
                            color: (theme) => theme.palette.primary.contrastText,
                            backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Familia</TableCell>
                        <TableCell align="left" sx={{
                            color: (theme) => theme.palette.primary.contrastText,
                            backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Ações</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {list.map((item) => {
                        const sx = {
                            padding: "4px 8px",
                        };
                        return <TableRow key={item.id} hover>
                            <TableCell sx={sx}>
                                <Stack direction="row" alignItems="center">
                                    <BadgedAvatar
                                        title={item.name}
                                        avatar={assertThumbnailUrl(item.url)}
                                        size="32px"
                                        marginRight={1}
                                    />
                                    {item.name}
                                </Stack>
                            </TableCell>
                            <TableCell sx={sx}>{item.nickname || item.name}</TableCell>
                            <TableCell sx={sx}>{isoToBrt(item.birthDate)}</TableCell>
                            <TableCell sx={sx}>{item.schoolNumber}</TableCell>
                            <TableCell sx={sx}>
                                <Stack direction="column" spacing={0.5} flexWrap="wrap">
                                    {item.classrooms ? item.classrooms.map(c => (
                                        <Stack key={c.id} direction="row" alignItems="center">
                                            <BadgedAvatar
                                                title={c.name}
                                                avatar={assertThumbnailUrl(c.url)}
                                                size="24px"
                                                marginRight={1}
                                            />
                                            {c.name}
                                        </Stack>
                                    )) : "-"}
                                </Stack>
                            </TableCell>
                            <TableCell sx={sx}>
                                <UserStack users={item.family} />
                            </TableCell>
                            <TableCell sx={sx}>
                                <ButtonGroup variant="text" size="small">
                                    <ButtonGroup variant="text" size="small">
                                        <Tooltip title="Ver detalhes do estudante">
                                            <Button onClick={() => {
                                                dispatch(studentSlice.actions.clean());
                                                navigate(routeStudent(schoolId, item.id));
                                            }}>
                                                <VisibilityIcon />
                                            </Button>
                                        </Tooltip>
                                    </ButtonGroup>
                                </ButtonGroup>
                            </TableCell>
                        </TableRow>;
                    })}
                </TableBody>
            </Table>
        </>;
    };

    const buttons = hasPermission(user.priza.info, PermissionId.SCHOOL_ADMIN, schoolId) ? [
        {
            label: <AddIcon />,
            click: () => {
                dispatch(classroomSlice.actions.clean());
                navigate(routeDptoNew(schoolId));
            }
        }
    ] : undefined;

    return <PrizaAvatarGrid
        toolbar={{
            label: <Stack direction={"row"} sx={{ width: { xs: "75%", sm: "100%" } }}>
                <Typography variant="h6" component="div" sx={{ m: 1, pr: 2 }}>
                    Alunos
                </Typography>
                <ElementSearchBox
                    filter={filterStudent}
                    setFilter={(filter: string) => {
                        setFilterStudent(filter);
                    }}
                    placeholder="Nome do aluno"
                    label="search"
                    search={(e: any) => {
                        e.preventDefault();
                        dispatch(schoolStudentsSlice.actions.clean());
                        // dispatch(retrieveSchoolStudents({ schoolId: schoolId, filter: filterStudent }));
                        // console.info(`Search '${filterStudent}' requested`);
                    }}
                    working={students.state.state == LoadState.Loading}
                />
            </Stack>,
            buttons: buttons
        }}
        buildContent={buildContent}
        state={students.state}
        retry={() => { dispatch(schoolStudentsSlice.actions.clean()); }}
        messages={{
            empty: "Nenhum aluno associado a esta escola.",
            error: "Erro ao carregar alunos."
        }}
    />;

};

export default SchoolStudentList;